(function () {
  'use strict';

  // NOTA IMPORTANTE: google.maps.places.Autocomplete (la classe "tutto in uno" che si
  // agganciava direttamente al campo indirizzo) è disponibile solo ai progetti Google
  // Cloud che avevano già la Places API attiva prima del 1 marzo 2025. Il progetto usato
  // da questo sito ha attivato la Places API il 3 gennaio 2026, quindi rientra tra i
  // "nuovi clienti" per cui Google ha disattivato quella classe: il widget si inizializza
  // senza errori JS ma non restituisce mai suggerimenti.
  //
  // Il rimpiazzo ufficiale (PlaceAutocompleteElement) è un web component con un proprio
  // input incorporato — non si può agganciare al campo WooCommerce esistente, andrebbe
  // ad aggiungere un secondo campo visivamente diverso, cosa esplicitamente non voluta.
  //
  // Soluzione: usiamo l'API di più basso livello (AutocompleteSuggestion, anch'essa
  // parte della "Places API (New)" e quindi NON soggetta al blocco) per interrogare
  // Google via JS puro, e costruiamo noi stessi una tendina di suggerimenti agganciata
  // al campo #billing_address_1 esistente — stesso identico campo/stile di sempre.

  const DEBOUNCE_MS = 250;
  const MIN_CHARS = 3;

  let sessionToken = null;
  let debounceTimer = null;
  let requestSeq = 0;
  let dropdownEl = null;
  let activeIndex = -1;
  let currentSuggestions = [];

  // AutocompleteSuggestion/AutocompleteSessionToken fanno parte della "Places UI Kit" più
  // recente: caricarle appendendo &libraries=places al classico script tag può non
  // esporle in modo affidabile su google.maps.places. Google raccomanda esplicitamente
  // google.maps.importLibrary('places') per queste classi — le teniamo qui invece che
  // fare riferimento a google.maps.places.X in giro per il file.
  // "Single-flight": initAutocomplete() viene chiamato da più punti (page load, evento
  // updated_checkout di WooCommerce, retry) e può quindi girare in parallelo più volte.
  // Senza questa guardia, una chiamata già andata a buon fine poteva essere sovrascritta/
  // corrotta da un'altra chiamata partita in parallelo e fallita più tardi (causa di un
  // TypeError interno di Google visto in produzione, con la sessionToken diventata
  // undefined proprio mentre l'utente stava già usando l'autocomplete funzionante).
  let placesLib = null;
  let placesLibPromise = null;

  function loadPlacesLibrary() {
    if (placesLib) return Promise.resolve(placesLib);
    if (!placesLibPromise) {
      if (!window.google?.maps?.importLibrary) return Promise.resolve(null);
      placesLibPromise = google.maps.importLibrary('places')
        .then(function (lib) {
          placesLib = lib;
          return lib;
        })
        .catch(function (e) {
          placesLibPromise = null; // permette un nuovo tentativo alla prossima chiamata
          throw e;
        });
    }
    return placesLibPromise;
  }

  function getSessionToken() {
    if (!sessionToken) {
      sessionToken = new placesLib.AutocompleteSessionToken();
    }
    return sessionToken;
  }

  function resetSessionToken() {
    sessionToken = null;
  }

  function closeDropdown() {
    if (dropdownEl) {
      dropdownEl.remove();
      dropdownEl = null;
    }
    activeIndex = -1;
    currentSuggestions = [];
  }

  function ensureDropdown(field) {
    if (dropdownEl) return dropdownEl;
    const wrapper = field.closest('.form-row') || field.parentElement;
    if (getComputedStyle(wrapper).position === 'static') {
      wrapper.style.position = 'relative';
    }
    dropdownEl = document.createElement('ul');
    dropdownEl.className = 'hb-address-suggestions';
    wrapper.appendChild(dropdownEl);
    return dropdownEl;
  }

  function renderSuggestions(field, suggestions) {
    currentSuggestions = suggestions;
    activeIndex = -1;

    if (!suggestions.length) {
      closeDropdown();
      return;
    }

    const list = ensureDropdown(field);
    list.innerHTML = '';

    suggestions.forEach(function (suggestion, index) {
      const prediction = suggestion.placePrediction;
      if (!prediction) return;

      const li = document.createElement('li');
      li.className = 'hb-address-suggestions__item';
      li.setAttribute('role', 'option');
      li.textContent = prediction.text ? prediction.text.text : '';

      li.addEventListener('mousedown', function (e) {
        // mousedown (non click): previene il blur del campo prima della selezione
        e.preventDefault();
        selectSuggestion(field, prediction);
      });

      if (index === activeIndex) li.classList.add('is-active');

      list.appendChild(li);
    });
  }

  async function selectSuggestion(field, prediction) {
    closeDropdown();

    const place = prediction.toPlace();
    await place.fetchFields({ fields: ['addressComponents'] });

    // Estraggo componenti indirizzo
    const components = {};
    (place.addressComponents || []).forEach(function (comp) {
      comp.types.forEach(function (type) {
        components[type] = comp.longText;
        if (type === 'administrative_area_level_1' || type === 'country') {
          components[type + '_short'] = comp.shortText;
        }
      });
    });

    // Compilazione campi WooCommerce
    const street = ((components.street_number || '') + ' ' + (components.route || '')).trim();
    const city = components.locality || components.postal_town || '';
    const postcode = components.postal_code || '';
    const state = components.administrative_area_level_2 || components.administrative_area_level_1 || '';
    const countryShort = components.country_short || '';
    const stateShort = components.administrative_area_level_1_short || '';

    const cityField = document.querySelector('#billing_city');
    const postcodeField = document.querySelector('#billing_postcode');

    if (street) field.value = street;
    if (cityField) cityField.value = city;
    if (postcodeField) postcodeField.value = postcode;

    // Dropdown billing_country: le <option> di WooCommerce hanno value = codice ISO
    // (es. "IT"), non il nome esteso — va usato components.country_short. Il trigger
    // via jQuery aggiorna la UI di select2 E fa scattare sincronamente il rebuild
    // nativo WC del campo stato, oltre ai listener di validazione telefono agganciati
    // al cambio paese in _checkout.js.
    const countryField = document.querySelector('#billing_country');
    if (countryField && countryShort) {
      const countryOption = Array.from(countryField.options).find(
        (o) => o.value.toLowerCase() === countryShort.toLowerCase()
      );
      if (countryOption) {
        jQuery(countryField).val(countryOption.value).trigger('change');
      }
    }

    // Dropdown billing_state (globale) — ricercato DOPO il change sul country: WC
    // ricostruisce sincronamente il markup dello stato quando il paese cambia.
    const stateSelect = document.querySelector('#billing_state');
    if (stateSelect) {
      const option = Array.from(stateSelect.options).find(
        (o) => o.value.toLowerCase() === stateShort.toLowerCase() || o.text.toLowerCase() === state.toLowerCase()
      );

      if (option) {
        stateSelect.value = option.value;
      } else {
        const newOption = new Option(state, stateShort || state, true, true);
        stateSelect.add(newOption);
        stateSelect.value = newOption.value;
      }

      stateSelect.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Nuovo session token per la prossima ricerca (best practice di fatturazione Google:
    // un token copre dalla prima digitazione alla selezione finale di UNA ricerca)
    resetSessionToken();

    jQuery('body').trigger('update_checkout');
  }

  async function fetchSuggestions(field, inputValue) {
    const seq = ++requestSeq;
    try {
      const { suggestions } = await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input: inputValue,
        sessionToken: getSessionToken(),
      });
      if (seq !== requestSeq) return; // risposta obsoleta, l'utente ha continuato a digitare
      renderSuggestions(field, suggestions || []);
    } catch (e) {
      console.error('GOOGLE PLACES ERRORE', e);
      closeDropdown();
    }
  }

  function moveActive(delta) {
    if (!dropdownEl || !currentSuggestions.length) return;
    const items = dropdownEl.querySelectorAll('.hb-address-suggestions__item');
    activeIndex = (activeIndex + delta + items.length) % items.length;
    items.forEach(function (item, i) {
      item.classList.toggle('is-active', i === activeIndex);
    });
    items[activeIndex]?.scrollIntoView({ block: 'nearest' });
  }

  function bindField(field) {
    if (field.dataset.hbAutocompleteAttached) return;
    field.dataset.hbAutocompleteAttached = '1';
    field.setAttribute('autocomplete', 'off');

    field.addEventListener('input', function () {
      const value = field.value.trim();
      clearTimeout(debounceTimer);
      if (value.length < MIN_CHARS) {
        closeDropdown();
        return;
      }
      debounceTimer = setTimeout(function () {
        fetchSuggestions(field, value);
      }, DEBOUNCE_MS);
    });

    field.addEventListener('keydown', function (e) {
      if (!dropdownEl) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        moveActive(1);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        moveActive(-1);
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0 && currentSuggestions[activeIndex]) {
          e.preventDefault();
          selectSuggestion(field, currentSuggestions[activeIndex].placePrediction);
        }
      } else if (e.key === 'Escape') {
        closeDropdown();
      }
    });

    field.addEventListener('blur', function () {
      // Delay per permettere al mousedown sulla tendina di completarsi prima della chiusura
      setTimeout(closeDropdown, 150);
    });
  }

  // Alcuni plugin di consenso cookie (es. autoblocking di Iubenda) intercettano il tag
  // <script> creato dal bootstrap loader e sparano un evento 'error' sintetico subito,
  // mentre la richiesta reale (quella visibile a 200 nel tab Network) va a buon fine poco
  // dopo per un percorso interno separato del plugin. IMPORTANTE: NON bisogna creare un
  // nuovo tag <script> per ritentare — se Iubenda ne esegue più di uno tra quelli creati,
  // la libreria Google Maps si inizializza più volte sulla stessa pagina, corrompendo il
  // suo stato interno (causa di un TypeError generico quando si tenta poi una ricerca).
  // Il vero secondo tentativo è quindi un polling passivo sullo stesso namespace globale,
  // che si popola comunque quando Iubenda esegue davvero il (singolo) script già creato.
  let fallbackPollInterval = null;

  function bindFieldIfReady() {
    const field = document.querySelector('#billing_address_1');
    if (!field) {
      console.log('[HB Autocomplete] campo #billing_address_1 non trovato');
      return;
    }
    console.log('[HB Autocomplete] aggancio il campo indirizzo');
    bindField(field);
  }

  function pollForLibraryFallback() {
    if (fallbackPollInterval) return;
    console.log('[HB Autocomplete] avvio polling di fallback (20s)');
    fallbackPollInterval = setInterval(function () {
      if (window.google?.maps?.places?.AutocompleteSuggestion) {
        console.log('[HB Autocomplete] polling: libreria trovata');
        clearInterval(fallbackPollInterval);
        fallbackPollInterval = null;
        placesLib = window.google.maps.places;
        bindFieldIfReady();
      }
    }, 500);
    setTimeout(function () {
      if (fallbackPollInterval) {
        console.log('[HB Autocomplete] polling: timeout dopo 20s, libreria mai apparsa');
        clearInterval(fallbackPollInterval);
        fallbackPollInterval = null;
      }
    }, 20000);
  }

  async function initAutocomplete() {
    console.log('[HB Autocomplete] initAutocomplete chiamato, importLibrary disponibile:', !!window.google?.maps?.importLibrary);
    try {
      const lib = await loadPlacesLibrary();
      console.log('[HB Autocomplete] loadPlacesLibrary risolto:', lib);
      if (!lib?.AutocompleteSuggestion) return;
      bindFieldIfReady();
    } catch (e) {
      console.log('[HB Autocomplete] loadPlacesLibrary fallito, avvio fallback:', e);
      pollForLibraryFallback();
    }
  }

  // Bootstrap loader ufficiale di Google per il caricamento dinamico via importLibrary()
  // (https://developers.google.com/maps/documentation/javascript/load-maps-js-api#dynamic-library-import).
  // Un semplice <script src="maps/api/js?..."> NON prepara correttamente lo stato interno
  // richiesto da importLibrary() (causava "Cannot read properties of undefined (reading
  // 'connectForExplicitThirdPartyLoad')"): questo script inline minimale è l'unico modo
  // documentato per usarlo. A differenza del vecchio approccio, il caricamento vero e
  // proprio avviene tramite un parametro "callback" richiamato dallo script di Google una
  // volta pronto — non dipende da un handler onload assegnato via JS, quindi funziona
  // correttamente anche quando un plugin di consenso cookie (es. autoblocking di Iubenda)
  // clona/ritarda il tag <script> creato internamente da questo loader.
  (function (g) {
    let h, a, k;
    const p = 'The Google Maps JavaScript API';
    const c = 'google';
    const l = 'importLibrary';
    const q = '__ib__';
    const m = document;
    let b = window;
    b = b[c] || (b[c] = {});
    const d = b.maps || (b.maps = {});
    const r = new Set();
    const e = new URLSearchParams();
    const u = () => h || (h = new Promise(async (f, n) => {
      a = m.createElement('script');
      e.set('libraries', [...r] + '');
      for (k in g) e.set(k.replace(/[A-Z]/g, (t) => '_' + t[0].toLowerCase()), g[k]);
      e.set('callback', c + '.maps.' + q);
      a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
      d[q] = f;
      // NON resettiamo h qui: un solo tag <script> va creato per l'intera pagina. Un
      // eventuale errore prematuro (falso allarme di Iubenda) viene gestito dal polling di
      // fallback in initAutocomplete(), non ricreando un secondo script.
      a.onerror = () => n(new Error(p + ' could not load.'));
      a.nonce = m.querySelector('script[nonce]')?.nonce || '';
      m.head.append(a);
    }));
    d[l] ? console.warn(p + ' only loads once. Ignoring:', g) : (d[l] = (f, ...n) => r.add(f) && u().then(() => d[l](f, ...n)));
  })({ key: MAPS_CFG.apiKey, v: 'weekly' });

  initAutocomplete();

  // Supporto AJAX WooCommerce
  document.body.addEventListener('updated_checkout', initAutocomplete);

})();
